import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Input, Row, Space, Typography, message } from 'antd';
import { CheckCircleOutlined, EditOutlined } from '@ant-design/icons';
import {
  completenessApi,
  customerApi,
  vehicleApi,
  type CompletenessMissingField,
  type CompletenessStage,
} from '../services/api';

const { Text } = Typography;

/**
 * WP-02 — inline capture of whatever a customer record is still missing.
 *
 * The owner's complaint: "im not sure if address is easily input into customers ...
 * make sure db and in estimates or work orders can easily add address or more
 * information for the customer."
 *
 * Two things were actually wrong. On the ESTIMATE screen the address fields did
 * exist, but inside a collapsed "Additional Details" accordion with nothing
 * indicating they mattered — so nobody opened it. On the WORK ORDER screen there
 * was no customer editor at all; the only route to an address was to navigate away
 * to the customer page and lose the draft.
 *
 * This panel fixes both by being droppable into either screen. It:
 *
 *   - asks the SERVER what is missing (it never restates the rules — that
 *     duplication across mobile/web/portal is the bug WP-02 exists to remove),
 *   - renders an input for exactly those fields and nothing else,
 *   - saves ONLY the customer, via its own PUT, and
 *   - never navigates. The surrounding estimate or work-order draft is untouched,
 *     which is the whole point: the reason the address never got entered is that
 *     entering it used to cost you your unsaved work.
 *
 * Renders NOTHING when the record is already complete for the stage, so it costs a
 * screen nothing on the common path.
 */
export interface CustomerCompletenessPanelProps {
  customerId?: number | null;
  /** Include to also check the vehicle rules for that stage. */
  vehicleId?: number | null;
  /** Which rule set to measure against. */
  stage: CompletenessStage;
  /**
   * Wording tuned to where this sits. `estimate` speaks about what will be needed
   * later; `work_order` speaks about what is needed now.
   */
  tone?: 'advisory' | 'blocking';
  /** Fired after a successful save so the parent can refresh its own copy. */
  onSaved?: (missingRemaining: CompletenessMissingField[]) => void;
  /** Bump to force a re-check (e.g. after the parent changes the customer). */
  refreshKey?: number | string;
}

/** Vehicle fields are numeric where the column is numeric. */
const NUMERIC_VEHICLE_FIELDS = new Set(['year']);

const CustomerCompletenessPanel: React.FC<CustomerCompletenessPanelProps> = ({
  customerId,
  vehicleId,
  stage,
  tone = 'advisory',
  onSaved,
  refreshKey,
}) => {
  const [form] = Form.useForm();
  const [missing, setMissing] = useState<CompletenessMissingField[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  // A check that failed is NOT the same as a record that is complete. Reporting
  // "all good" because the request errored would be the worst possible outcome
  // here — it is exactly the false clean bill of health this WP is about.
  const [checkFailed, setCheckFailed] = useState(false);

  const check = useCallback(async () => {
    if (!customerId) {
      setMissing([]);
      setCheckFailed(false);
      return;
    }
    setLoading(true);
    try {
      const res = await completenessApi.forCustomer(customerId, {
        stage,
        vehicleId: vehicleId ?? undefined,
      });
      setMissing(res.data.missing ?? []);
      setCheckFailed(false);
    } catch (err) {
      console.error('Completeness check failed', err);
      setMissing([]);
      setCheckFailed(true);
    } finally {
      setLoading(false);
    }
  }, [customerId, vehicleId, stage]);

  useEffect(() => {
    check();
  }, [check, refreshKey]);

  const customerFields = useMemo(() => missing.filter((m) => m.entity === 'customer'), [missing]);
  const vehicleFields = useMemo(() => missing.filter((m) => m.entity === 'vehicle'), [missing]);

  const handleSave = async () => {
    if (!customerId) return;
    try {
      const values = await form.validateFields();
      setSaving(true);

      const customerPatch: Record<string, unknown> = {};
      for (const field of customerFields) {
        const value = values[`customer.${field.field}`];
        if (typeof value === 'string' ? value.trim() !== '' : value != null) {
          customerPatch[field.field] = typeof value === 'string' ? value.trim() : value;
        }
      }

      const vehiclePatch: Record<string, unknown> = {};
      for (const field of vehicleFields) {
        const raw = values[`vehicle.${field.field}`];
        if (typeof raw === 'string' ? raw.trim() !== '' : raw != null) {
          vehiclePatch[field.field] = NUMERIC_VEHICLE_FIELDS.has(field.field)
            ? Number(raw)
            : String(raw).trim();
        }
      }

      if (Object.keys(customerPatch).length > 0) {
        await customerApi.update(customerId, customerPatch as never);
      }
      if (vehicleId && Object.keys(vehiclePatch).length > 0) {
        await vehicleApi.update(vehicleId, vehiclePatch as never);
      }

      // Re-ask the server rather than assuming the save cleared everything —
      // a value the API rejected or normalised away would otherwise show as done.
      const res = await completenessApi.forCustomer(customerId, {
        stage,
        vehicleId: vehicleId ?? undefined,
      });
      const remaining = res.data.missing ?? [];
      setMissing(remaining);
      setCheckFailed(false);
      form.resetFields();
      if (remaining.length === 0) {
        setOpen(false);
        message.success('Customer details saved');
      }
      onSaved?.(remaining);
    } catch (err: any) {
      if (err?.errorFields) return; // form validation — already shown inline
      console.error('Failed to save customer details', err);
      const detail = err?.response?.data?.details?.[0]?.message ?? err?.response?.data?.error;
      message.error(detail ? `Could not save: ${detail}` : 'Could not save customer details');
    } finally {
      setSaving(false);
    }
  };

  if (!customerId) return null;

  if (checkFailed) {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="Could not check the customer's details"
        description={
          <Space>
            <Text type="secondary">
              Some information may still be missing. This does not block your work.
            </Text>
            <Button size="small" onClick={check} loading={loading}>
              Retry
            </Button>
          </Space>
        }
      />
    );
  }

  if (missing.length === 0) return null;

  const summary = missing.map((m) => m.label).join(', ');
  const heading =
    tone === 'blocking'
      ? 'Missing details — needed before this can be invoiced or paid'
      : 'Missing customer details';

  return (
    <Alert
      type={tone === 'blocking' ? 'warning' : 'info'}
      showIcon
      style={{ marginBottom: 12 }}
      message={heading}
      description={
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            {summary}
          </Text>

          {!open ? (
            <Button size="small" icon={<EditOutlined />} onClick={() => setOpen(true)}>
              Add now
            </Button>
          ) : (
            <Form form={form} layout="vertical" size="small" style={{ marginTop: 4 }}>
              <Row gutter={12}>
                {missing.map((field) => (
                  <Col xs={24} sm={12} key={`${field.entity}.${field.field}`}>
                    <Form.Item
                      // The LABEL comes from the server. A hard-coded label here
                      // would drift the moment the rules change.
                      label={field.label}
                      name={`${field.entity}.${field.field}`}
                      style={{ marginBottom: 8 }}
                    >
                      <Input
                        placeholder={field.label}
                        inputMode={
                          NUMERIC_VEHICLE_FIELDS.has(field.field) ? 'numeric' : undefined
                        }
                      />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
              <Space>
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  loading={saving}
                  onClick={handleSave}
                >
                  Save details
                </Button>
                <Button size="small" onClick={() => setOpen(false)} disabled={saving}>
                  Not now
                </Button>
              </Space>
              <div style={{ marginTop: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Saves to the customer only — nothing else on this screen is affected.
                </Text>
              </div>
            </Form>
          )}
        </div>
      }
    />
  );
};

export default CustomerCompletenessPanel;
